import { CtaType } from "@/components/molecules/CtaButtonGroup";
import SectionHeader from "./shared/SectionHeader";

/**
 * DarkNumberedStepsSection — full-bleed dark section with a large
 * centered title + subtitle + CTA pair, followed by a row of 4
 * numbered steps ("Step 1" / "Step 2" …). Each step has a small
 * green-yellow label, a heading, and a description. Used by feature
 * V2 pages for the "in 4 simple steps" block.
 */

export type DarkStep = {
  label?: string;
  heading: string;
  description: string;
};

export type DarkNumberedStepsSectionData = {
  titleMuted?: string;
  titleBright?: string;
  title?: string;
  subtitle?: string;
  ctas?: CtaType[];
  steps: DarkStep[];
  /** Override the accent color of the "Step N" label. */
  accentColor?: string;
};

const DarkNumberedStepsSection = ({
  titleMuted,
  titleBright,
  title,
  subtitle,
  ctas,
  steps,
  accentColor = "text-[#D4F75E]",
}: DarkNumberedStepsSectionData) => (
  <section className="relative w-full overflow-hidden bg-neutralBlack">
    <div className="container mx-auto px-4 py-10 sm:px-6 md:px-8 md:py-20 lg:px-12 lg:py-24">
      <SectionHeader
        titleMuted={titleMuted}
        titleBright={titleBright}
        title={title}
        subtitle={subtitle}
        ctas={ctas}
        variant="dark-lime"
        align="center"
      />
      <div
        className={`mx-auto mt-12 grid max-w-6xl gap-5 md:mt-16 md:gap-6 ${
          steps.length === 3 ? "md:grid-cols-3" : "md:grid-cols-4"
        }`}
      >
        {steps.map((s, idx) => (
          <div
            key={s.heading}
            className="relative flex flex-col overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-sm md:p-8"
          >
            <div
              className={`font-inter text-[14px] font-medium ${accentColor}`}
            >
              {s.label ?? `Step ${idx + 1}`}
            </div>
            <h3 className="mt-3 font-inter text-[18px] font-semibold leading-tight text-white md:text-[20px]">
              {s.heading}
            </h3>
            <p className="mt-3 font-inter text-[14px] leading-relaxed text-white/70 md:text-[15px]">
              {s.description}
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default DarkNumberedStepsSection;
